import React, { useState, useContext } from 'react';
import { motion } from 'framer-motion';
import Logo from '../components/Logo';
import CharacterGreeting from '../components/characters/CharacterGreeting';
import { useNavigate, useLocation } from 'react-router-dom';
import { CurpContext } from '../context/CurpContext';
import { toast } from 'react-hot-toast';

const HomePage = () => {
    const [inputCurp, setInputCurp] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false); // Estado para controlar el envío
    const { setCurp } = useContext(CurpContext);
    const navigate = useNavigate();
    const location = useLocation();

    const isSimplified = location.pathname === '/rapido';

    const route1 = isSimplified
        ? '/services'
        : '/services';

    const route2 = isSimplified
        ? '/test/simplified/consent'
        : '/form';

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log('CURP ingresado:', inputCurp); // Log para depuración

        if (inputCurp.length < 18) {
            toast.error('El CURP debe tener 18 caracteres.');
            return;
        }

        setIsSubmitting(true); // Inhabilitar el botón
        const upperCaseCurp = inputCurp.toUpperCase();
        setCurp(upperCaseCurp);

        console.log('CURP guardado en el contexto:', upperCaseCurp); // Log para depuración

        // Mostrar toast de loading
        toast.loading('¡Espera un momento, estamos preparando tu acceso VIP... sí, claro! 🎟️', {
            duration: 3000, // Duración del toast
            position: 'bottom-center'
        });

        try {
            const response = await fetch(`https://admin-django-inspira.azurewebsites.net/api/users/search/${upperCaseCurp}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });
            const data = await response.json();

            if (data.exists) {
                navigate(route1);
                toast.success('Usuarix registradx, selecciona el evento o servicio',
                    { position: 'bottom-center' })
                    ;
            } else {
                navigate(route2);
                toast('Vamos al registro!', {
                    icon: '🧚‍♂️',
                });
            }
        } catch (error) {
            console.error('Error al buscar el CURP:', error);
            toast.error('Hubo un error al procesar tu solicitud. Inténtalo de nuevo.');
        } finally {
            setIsSubmitting(false); // Habilitar el botón nuevamente
        }
    };

    const handleForeignSubmit = () => {
        setCurp('');
        navigate('/form', { state: { isForeign: true } });
    };

    const handleInputChange = (e) => {
        setInputCurp(e.target.value.toUpperCase());
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            className="relative flex flex-col items-center w-full min-h-screen bg-[#fff5f5] overflow-hidden"
        >
            {/* Elementos de fondo blureados */}
            <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-[#f8485e] rounded-full mix-blend-multiply filter blur-[120px] opacity-20 animate-slower-pulse animate-float" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-[#f8485e] rounded-full mix-blend-multiply filter blur-[120px] opacity-20 animate-slower-pulse animate-float" style={{ animationDelay: '4s' }} />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-[#f8485e] rounded-full mix-blend-multiply filter blur-[150px] opacity-10 animate-slow-pulse" />

            <div className="relative z-10 flex flex-col items-center w-full">
                <Logo />
                <motion.div
                    initial={{ x: -100 }}
                    animate={{ x: 0 }}
                    transition={{ duration: 1 }}
                    className="w-full lg:w-2/4 text-left ml-10 mt-5"
                >
                    <motion.h1
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="text-8xl font-bold text-[#f8485e] drop-shadow-sm"
                    >
                        ¡Hola!
                    </motion.h1>
                    <h1 className="text-lg font-bold mt-8 mr-3 pr-10 text-left text-gray-800 mb-1 drop-shadow-sm">
                        Tu salud sexual, en tus manos.
                    </h1>
                    <h1 className="text-lg font-bold mt-1 mr-3 pr-10 text-left text-gray-700 mb-10 drop-shadow-sm">
                        Accede a los servicios de Inspira de forma rápida y sencilla: Consejería en cuidado placentero, PrEP, PEP y ahora tu
                        <motion.span
                            initial={{ textShadow: '0px 0px 0px rgba(248, 72, 94, 0)' }}
                            animate={{ textShadow: '0px 0px 12px rgba(248, 72, 94, 0.4)' }}
                            transition={{ duration: 4.5, repeat: Infinity, repeatType: 'mirror' }}
                            className="text-[#f8485e]"
                        > Autoprueba de ITS. </motion.span>
                        Para comenzar, ingresa tu CURP 👇
                    </h1>
                </motion.div>
                <motion.div
                    className="w-full flex flex-col items-center"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                        duration: 0.3,
                        ease: [0, 0.71, 0.2, 1.01],
                        scale: {
                            type: "spring",
                            damping: 5,
                            stiffness: 100,
                            restDelta: 0.001,
                        },
                    }}
                >
                    <CharacterGreeting className="mt-2 drop-shadow-xl sm:mt-0" />
                </motion.div>

                <form onSubmit={handleSubmit} className="mt-8 mb-8">
                    <input
                        type="text"
                        placeholder="Ingrese su CURP"
                        value={inputCurp}
                        onChange={handleInputChange}
                        className="p-2 rounded-l-lg mb-4 drop-shadow-md border border-gray-100 outline-none focus:ring-2 focus:ring-[#f8485e]/20"
                        minLength={18}
                        disabled={isSubmitting} // Inhabilitar el input mientras se envía
                    />
                    <button
                        type="submit"
                        className={`p-2 rounded-r-lg mb-4 drop-shadow-md transition-all duration-300 ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#f8485e] hover:bg-[#d93a4f] active:scale-95'
                            } text-white`}
                        disabled={isSubmitting} // Inhabilitar el botón mientras se envía
                    >
                        {isSubmitting ? 'Envianding' : 'Enviar ✨'}
                    </button>
                </form>
                <div className="flex-grow"></div>
                <button
                    onClick={handleForeignSubmit}
                    className="flex items-center gap-3 bg-[#f8485e] hover:bg-[#d93a4f] text-white px-6 py-3 rounded-2xl mb-24 drop-shadow-lg transition-all duration-300 active:scale-95 font-bold"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                        />
                    </svg>
                    Soy extranjero sin CURP
                </button>
            </div>
        </motion.div>
    );
};

export default HomePage;